import { FC, useMemo } from "react";
import { useExerciseState } from "@/contexts/exercise/Context";
import { ExerciseDONE, ExerciseTODO, WarmUpProps } from "@/types";
import GapFillIcon from "@/components/icons/exerciseV2/GapFillIcon";
import GroupingIcon from "@/components/icons/exerciseV2/GroupingIcon";
import ExerciseTrack from "./ExerciseTrack";
import TrueOrFalseIcon from "@/components/icons/exerciseV2/TrueOrFalseIcon";
import ExercisesContainer from "@/contexts/exercise/Container";
import MatchingOptionIcon from "@/components/icons/exerciseV2/MatchingOptionIcon";
import MultipleChoiceIcon from "@/components/icons/exerciseV2/MultipleChoiceIcon";

export const getExercisesOptions = (
  exercise: ExerciseTODO | ExerciseDONE | undefined,
) =>
  [
    {
      id: 1,
      type: "multiple_choice",
      Icon: MultipleChoiceIcon,
      title: "Multiple choice",
      description: "Choose the correct sentence",
      isEmpty: !exercise?.multiple_choice?.length,
    },
    {
      id: 2,
      type: "true_false",
      Icon: TrueOrFalseIcon,
      title: "True or False",
      description: "Choose the correct sentence",
      isEmpty: !exercise?.true_false?.length,
    },
    {
      id: 3,
      type: "matching_option",
      Icon: MatchingOptionIcon,
      title: "Matching option",
      description: "Match the answers correctly",
      isEmpty: !exercise?.matching_option?.length,
    },
    {
      id: 4,
      type: "gap_fill",
      Icon: GapFillIcon,
      title: "Gap fill",
      description: "Choose the correct sentence",
      isEmpty: !exercise?.gap_fill?.length,
    },
    {
      id: 5,
      type: "groupings",
      Icon: GroupingIcon,
      title: "Grouping",
      description: "Match the answers correctly",
      isEmpty: !exercise?.groupings?.length,
    },
  ].filter((option) => !option?.isEmpty);

const WarmUp: FC<WarmUpProps> = ({ exercise }) => {
  const { isExerciseStarted, exerciseResults } = useExerciseState();

  const isListening = exercise?.type === "listening";
  const exerciseTrack = exercise?.text || "";
  const audioUrl = exercise?.audio_file || "";
  const imageUrl = exercise?.img_file || "";
  const exerciseType = exercise?.modality.toLowerCase() || "";
  const definitions = exercise?.definitions || [];

  const exercisesOptions = useMemo(
    () => getExercisesOptions(exercise),
    [exercise],
  );

  const doneArray = useMemo(
    () => exerciseResults?.map((e) => e.type),
    [exerciseResults],
  );

  return (
    <div className="flex flex-col mt-3 text-xl font-semibold leading-6 text-brand gap-2.5">
      <div
        className={`justify-center p-6 bg-white rounded-lg h-auto ${
          isListening ? "max-h-[265px]" : ""
        }`}
      >
        <ExerciseTrack
          audioUrl={audioUrl}
          imageUrl={imageUrl}
          definitions={definitions}
          exerciseType={exerciseType}
          exerciseTrack={exerciseTrack}
        />
      </div>

      {isExerciseStarted ? (
        <ExercisesContainer exercise={exercise as ExerciseTODO} />
      ) : (
        exercisesOptions.map(({ id, Icon, title, description, type }) => {
          const isDone = (doneArray as Array<string>).includes(type);

          const color = isDone ? "#E6E6E6" : "";

          return (
            <div
              key={id}
              className={`flex justify-between items-center max-h-[67px] p-5 rounded-lg border border-brand-secondary border-solid ${
                isDone ? `bg-[#EEEEEE66] border-[${color}]` : "bg-white"
              }`}
            >
              <div
                className={`flex justify-start items-center gap-5 ${
                  isDone ? `text-[${color}]` : ""
                }`}
              >
                <Icon
                  color={isDone ? color : "#8065C9"}
                  className={`w-[32px] h-[32px]`}
                />

                <div className="italic max-md:max-w-full text-left">
                  <h2 className="text-sm">{title}</h2>

                  <p
                    className={`text-xs italic text-brand ${
                      isDone ? `text-[${color}]` : ""
                    }`}
                  >
                    {description}
                  </p>
                </div>
              </div>

              <div className="!opacity-100">
                {isDone && (
                  <div className="md:w-[64px] md:h-[22px] text-[10px] font-semibold flex items-center justify-center bg-shade-five rounded-[10px]">
                    <div>DONE</div>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default WarmUp;
